import React, {useEffect, useState} from 'react'
import styled, {keyframes, css} from 'styled-components'
import {AtlasCheckbox, AtlasTextfield, Box, Text, Dropdown, RadioGroup, AtlasRadio, sx, themeGet} from '@fuegokit/react'
import {AkOpenIcon, AkChevronDownIcon} from '@fuegokit/fuegoicons-react'

// type StyledDropdownExampleProps = DropdownProps &
//   DropdownTriggerProps &
//   DropdownContentProps &
//   DropdownGroupProps &
//   DropdownLabelProps &
//   DropdownItemProps &
//   DropdownCheckboxItemProps &
//   DropdownRadioGroupProps &
//   DropdownRadioItemProps &
//   DropdownItemIndicatorProps &
//   DropdownSeparatorProps &
//   DropdownArrowProps &
//   DropdownSubProps &
//   DropdownSubTriggerProps &
//   DropdownSubContentProps

const StyledDropdownExample = ({
  side,
  align,
  alignOffset,
  open: openProp,
  onOpenChange,
  sideOffset,
  onEscapeKeyDown,
  ...props
}) => {
  const [domLoaded, setDomLoaded] = useState(false)

  useEffect(() => {
    setDomLoaded(true)
  }, [])
  return (
    <>
      {domLoaded && (
        <Box>
          <StyledDropdown open={openProp ? openProp : undefined}>
            <StyledDropdownTrigger>
              Reporting
              <AkChevronDownIcon />
            </StyledDropdownTrigger>
            <Dropdown.Portal>
              <StyledDropdownContent
                side={side}
                align={align}
                alignOffset={alignOffset}
                sideOffset={sideOffset}
                onEscapeKeyDown={onEscapeKeyDown}
              >
                <StyledDropdownItem onSelect={() => alert("navigate to my application's SPA route")}>
                  Option 1
                </StyledDropdownItem>
                <StyledDropdownItem
                  description={`This is a description`}
                  onSelect={() => alert("navigate to my application's SPA route")}
                >
                  Option 2
                </StyledDropdownItem>
                <StyledDropdownItem onSelect={() => alert("navigate to my application's SPA route")}>
                  Option 3
                </StyledDropdownItem>
                <StyledDropdownItem disabled description={`This item is disabled`}>
                  Disabled
                </StyledDropdownItem>
                <StyledDropdownItem onSelect={() => window.open('https://www.google.com', '_blank', 'noreferrer')}>
                  <Box sx={{display: 'flex', alignItems: 'center'}}>
                    Navigate to a site <AkOpenIcon />
                  </Box>
                </StyledDropdownItem>
                <StyledDropdownItem onSelect={() => alert("navigate to my application's SPA route")}>
                  Option 6
                </StyledDropdownItem>
                <StyledDropdownSeparator />
                <StyledDropdownItem onSelect={() => alert("navigate to my application's SPA route")}>
                  Option 7
                </StyledDropdownItem>
              </StyledDropdownContent>
            </Dropdown.Portal>
          </StyledDropdown>
        </Box>
      )}
    </>
  )
}

export const ControlledStyledDropdownExample = () => {
  // local state for demonstrating both controlled and uncontrolled behavior
  const [localOpen, setLocalOpen] = React.useState(false)

  React.useEffect(() => {
    // Update local state to sync with the controlled behavior
    setLocalOpen(localOpen)
  }, [localOpen])

  const onCheckboxChange = React.useCallback(() => {
    // Toggle the open state when Checkbox is changed
    setLocalOpen(current => !current)
  }, [])

  const onEscapeKeyDown = React.useCallback(() => {
    // Close the Dropdown and uncheck the Checkbox when Escape key is pressed
    setLocalOpen(false)
    // Also update Checkbox state to unchecked
    // You can use a ref to store the Checkbox instance and call its click method
    // or update the state directly if you have a state variable for Checkbox state
  }, [])

  return (
    <Box sx={{p: 4, borderRadius: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2}}>
      <AtlasCheckbox label="Open" onChange={onCheckboxChange} id="checkbox" isChecked={localOpen} />
      <StyledDropdownExample open={localOpen} onEscapeKeyDown={onEscapeKeyDown} />
    </Box>
  )
}

const slideUpAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`
const slideRightAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(-2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`
const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`
const slideLeftAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`
const StyledDropdown = styled(Dropdown)``
const StyledDropdownItem = styled(Dropdown.Item)`
  font-size: ${themeGet('fontSizes.3')};
  line-height: 1;
  min-height: 40px;
  color: ${themeGet('colors.text.default')};
  display: flex;
  align-items: center;

  height: ${themeGet('space.5')};
  padding: 0 ${themeGet('space.2')};
  position: relative;
  padding-left: ${themeGet('space.3')};
  user-select: none;
  outline: none;
  text-align: left;

  margin: 0;
  text-decoration: none;

  &[data-highlighted] {
    color: ${themeGet('colors.text.default')};
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
    cursor: pointer;
  }
  &[data-disabled] {
    color: ${themeGet('colors.text.disabled')};
  }
  &:hover {
    text-decoration: none;
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
  }
  &:active {
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
  }
  &:visited {
    color: ${themeGet('colors.text.selected')};
  }
  ${sx}
`
const StyledDropdownTrigger = styled(Dropdown.Trigger)`
  // reset
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  user-select: none;
  display: flex;
  align-items: center;
  color: inherit;
  /* margin-left: ${themeGet('space.4')}; */
  font-weight: normal;
  font-size: ${themeGet('fontSizes.3')};
  text-decoration: none;
  opacity: 0.85;
  font-weight: ${themeGet('fontWeights.medium')};
  padding: ${themeGet('space.2')} 12px;
  border-radius: ${themeGet('radii.1')};
  &:hover {
    opacity: 1;
    text-decoration: none;
    color: ${themeGet('colors.link.default')};
    background-color: ${p =>
      p.isActive
        ? themeGet('colors.background.selected.default.hovered')
        : themeGet('colors.background.neutral.subtle.hovered')};
  }

  &:focus {
    opacity: 1;
    text-decoration: none;
    background-color: ${p => (p.isActive ? themeGet('colors.background.selected.default.[default]') : '')};
    &:hover {
      background-color: ${p =>
        p.isActive
          ? themeGet('colors.background.selected.default.hovered')
          : themeGet('colors.background.neutral.subtle.hovered')};
    }
  }
  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  ${p => {
    if (p.isActive) {
      return css`
        opacity: 1;
        text-decoration: none;
        color: ${themeGet('colors.text.selected')};
        background-color: ${themeGet('colors.background.selected.default.[default]')};
      `
    }
    return ''
  }}
  svg {
    transition: transform 0.15s ease;
  }
  &[data-state='open'] {
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
  }
  ${sx}
`
const StyledDropdownContent = styled(Dropdown.Content)`
  min-width: 184px;
  max-width: 800px;
  max-height: calc(100vh - 16px);
  position: absolute;
  top: 10px;
  right: 0px;
  left: -56px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: 1px solid ${themeGet('colors.border.default')};
  border-radius: ${themeGet('radii.2')};
  padding: ${themeGet('space.2')} 0;
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
  &[data-side='top'] {
    animation-name: ${slideDownAndFade};
  }
  &[data-side='right'] {
    animation-name: ${slideLeftAndFade};
  }
  &[data-side='bottom'] {
    animation-name: ${slideUpAndFade};
  }
  &[data-side='left'] {
    animation-name: ${slideRightAndFade};
  }
  ${sx}
`
const StyledDropdownSeparator = styled(Dropdown.Separator)`
  height: 1px;
  background-color: ${themeGet('colors.border.default')};
  margin: ${themeGet('space.2')};
  ${sx}
`

export default StyledDropdownExample

// parking lot
// export const SideStyledDropdownExample = () => {
//   // local state for demonstrating both controlled and uncontrolled behavior
//   const [localOpen, setLocalOpen] = React.useState(false)

//   React.useEffect(() => {
//     // Update local state to sync with the controlled behavior
//     setLocalOpen(localOpen)
//   }, [localOpen])

//   const onCheckboxChange = React.useCallback(() => {
//     // Toggle the open state when Checkbox is changed
//     setLocalOpen(current => !current)
//   }, [])

//   const [offsetValue, setOffsetValue] = React.useState(10) // Initial offset value
//   const [selectedSide, setSelectedSide] = React.useState('bottom') // Default side value

//   React.useEffect(() => {
//     setLocalOpen(localOpen)
//   }, [localOpen])

//   const onOffsetChange = event => {
//     const newValue = parseInt(event.target.value, 10) || 0
//     setOffsetValue(newValue)
//   }

//   const onEscapeKeyDown = React.useCallback(() => {
//     setLocalOpen(false)
//   }, [])

//   const onSideChange = event => {
//     setSelectedSide(event.target.value)
//   }

//   return (
//     <Box sx={{p: 4, borderRadius: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4}}>
//       <Box>
//         <Text>Adjust the side offset value:</Text>
//         <AtlasTextfield
//           label="Offset Value"
//           type="number"
//           value={offsetValue.toString()}
//           onChange={onOffsetChange}
//           id="offset"
//         />
//       </Box>
//       <Box>
//         <Text>Change the side:</Text>
//         <RadioGroup label="Select Side" name="side" value={selectedSide} onChange={onSideChange} direction={`row`}>
//           <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
//             <AtlasRadio value="one" id="right" />
//             <label htmlFor="right">Right</label>
//           </Box>
//           <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
//             <AtlasRadio value="one" id="top" />
//             <label htmlFor="top">Top</label>
//           </Box>
//           <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
//             <AtlasRadio defaultChecked value="two" id="bottom" />
//             <label htmlFor="bottom">Bottom</label>
//           </Box>
//           <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
//             <AtlasRadio value="three" id="left" />
//             <label htmlFor="left">Left</label>
//           </Box>
//         </RadioGroup>
//       </Box>

//       <Box>
//         <Text>Toggle the controlled open state:</Text>
//         <AtlasCheckbox label="Open" onChange={onCheckboxChange} id="checkbox" isChecked={localOpen} />
//       </Box>

//       {/* Add alignment and sideOffset props to demonstrate */}
//       <StyledDropdownExample
//         open={localOpen}
//         onEscapeKeyDown={onEscapeKeyDown}
//         align="start"
//         sideOffset={offsetValue}
//         open={localOpen}
//       />
//     </Box>
//   )
// }
